/**
 * Image Viewer Component - Glass Morphism Design
 * Displays generated images with download options
 * Validates: Requirements 16.4
 */
import { useState } from 'react';
import { ImageJob } from '../../stores/imageStore';
import { Icons } from '../icons';
import { THEME } from '../../styles/theme';

interface ImageViewerProps {
  job: ImageJob | null;
  onClose?: () => void;
}

export function ImageViewer({ job, onClose }: ImageViewerProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  if (!job) {
    return null;
  }

  const images = job.images || [];
  const currentImage = images[selectedIndex];

  const handleDownload = async (url: string, index: number) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `image-${job.id}-${index + 1}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    } catch {
      // Fallback: open in new tab
      window.open(url, '_blank');
    }
  };

  const handleDownloadAll = async () => {
    for (let i = 0; i < images.length; i++) {
      const image = images[i];
      if (image) {
        await handleDownload(image.url, i);
        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
  };

  // Show placeholder if no images yet
  if (images.length === 0) {
    return (
      <div
        className="rounded-2xl p-6 border"
        style={{
          backgroundColor: 'rgba(255,255,255,0.03)',
          borderColor: THEME.border,
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold" style={{ color: THEME.textMain }}>
            Image Preview
          </h3>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-lg transition-colors"
              style={{ color: THEME.textMuted }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                e.currentTarget.style.color = THEME.textMain;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = THEME.textMuted;
              }}
            >
              <Icons.X size={20} />
            </button>
          )}
        </div>

        <div
          className="aspect-square max-w-2xl mx-auto rounded-xl flex items-center justify-center"
          style={{ backgroundColor: THEME.bgSurface }}
        >
          {job.status === 'processing' ? (
            <div className="text-center">
              <Icons.Refresh
                size={48}
                className="animate-spin mx-auto mb-3"
                style={{ color: THEME.accentColor }}
              />
              <p style={{ color: THEME.textSecondary }}>Generating image...</p>
            </div>
          ) : job.status === 'failed' ? (
            <div className="text-center">
              <Icons.X
                size={48}
                className="mx-auto mb-3"
                style={{ color: THEME.error }}
              />
              <p style={{ color: THEME.error }}>Generation failed</p>
              {job.error && (
                <p className="text-xs mt-2 max-w-md" style={{ color: THEME.textMuted }}>
                  {job.error}
                </p>
              )}
            </div>
          ) : (
            <div className="text-center">
              <Icons.Image
                size={48}
                className="mx-auto mb-3"
                style={{ color: THEME.textMuted }}
              />
              <p style={{ color: THEME.textMuted }}>Waiting for image...</p>
            </div>
          )}
        </div>

        {/* Job Info */}
        <div
          className="mt-4 p-3 rounded-xl"
          style={{ backgroundColor: THEME.bgSurface }}
        >
          <p className="text-sm mb-2" style={{ color: THEME.textMain }}>
            {job.enhancedPrompt || job.prompt}
          </p>
          <div className="flex items-center gap-3 text-xs" style={{ color: THEME.textMuted }}>
            <span>{job.model}</span>
            {job.settings.size && <span>• {job.settings.size}</span>}
            {job.settings.quality && <span>• {job.settings.quality}</span>}
            {job.settings.style && <span>• {job.settings.style}</span>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl p-6 border"
      style={{
        backgroundColor: 'rgba(255,255,255,0.03)',
        borderColor: THEME.border,
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold" style={{ color: THEME.textMain }}>
          Image Preview
        </h3>
        <div className="flex items-center gap-2">
          {images.length > 1 && (
            <button
              onClick={handleDownloadAll}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-xl border transition-all duration-200"
              style={{
                backgroundColor: 'rgba(255,255,255,0.03)',
                borderColor: THEME.border,
                color: THEME.textMain,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                e.currentTarget.style.borderColor = THEME.borderHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)';
                e.currentTarget.style.borderColor = THEME.border;
              }}
            >
              <Icons.Download size={16} />
              <span>Download All</span>
            </button>
          )}
          {currentImage && (
            <button
              onClick={() => handleDownload(currentImage.url, selectedIndex)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-xl transition-all duration-200"
              style={{
                background: THEME.accent,
                color: THEME.textMain,
                boxShadow: THEME.shadowAccent,
              }}
            >
              <Icons.Download size={16} />
              <span>Download</span>
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-lg transition-colors"
              style={{ color: THEME.textMuted }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                e.currentTarget.style.color = THEME.textMain;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = THEME.textMuted;
              }}
            >
              <Icons.X size={20} />
            </button>
          )}
        </div>
      </div>

      {/* Main Image */}
      <div
        className="relative aspect-square max-w-2xl mx-auto rounded-xl overflow-hidden"
        style={{ backgroundColor: THEME.bgSurface }}
      >
        {currentImage && (
          <img
            src={currentImage.url}
            alt={job.prompt}
            className="w-full h-full object-contain"
          />
        )}

        {/* Navigation Arrows (if multiple images) */}
        {images.length > 1 && (
          <>
            <button
              onClick={() => setSelectedIndex((prev) => (prev - 1 + images.length) % images.length)}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full transition-all duration-200"
              style={{
                backgroundColor: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(10px)',
                color: THEME.textMain,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.8)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.6)';
              }}
            >
              <Icons.ChevronDown size={24} className="rotate-90" />
            </button>
            <button
              onClick={() => setSelectedIndex((prev) => (prev + 1) % images.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full transition-all duration-200"
              style={{
                backgroundColor: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(10px)',
                color: THEME.textMain,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.8)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.6)';
              }}
            >
              <Icons.ChevronDown size={24} className="-rotate-90" />
            </button>
          </>
        )}
      </div>

      {/* Thumbnail Strip (if multiple images) */}
      {images.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => setSelectedIndex(index)}
              className="w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-200"
              style={{
                borderColor: index === selectedIndex ? THEME.accentColor : 'transparent',
              }}
              onMouseEnter={(e) => {
                if (index !== selectedIndex) {
                  e.currentTarget.style.borderColor = THEME.border;
                }
              }}
              onMouseLeave={(e) => {
                if (index !== selectedIndex) {
                  e.currentTarget.style.borderColor = 'transparent';
                }
              }}
            >
              <img
                src={image.url}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {/* Image Info */}
      <div
        className="mt-4 p-3 rounded-xl"
        style={{ backgroundColor: THEME.bgSurface }}
      >
        <p className="text-sm mb-2" style={{ color: THEME.textMain }}>
          {currentImage?.revisedPrompt || job.enhancedPrompt || job.prompt}
        </p>
        <div className="flex items-center gap-3 text-xs" style={{ color: THEME.textMuted }}>
          <span>{job.model}</span>
          {job.settings.size && <span>• {job.settings.size}</span>}
          {job.settings.quality && <span>• {job.settings.quality}</span>}
          {job.settings.style && <span>• {job.settings.style}</span>}
          {images.length > 1 && <span>• {selectedIndex + 1} of {images.length}</span>}
        </div>
      </div>
    </div>
  );
}
